import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ErrorBoundary } from '../components/common/ErrorBoundary';
import { AuthProvider } from '../contexts/AuthContext';
import { TenantProvider } from '../contexts/TenantContext';
import { ToastProvider } from '../contexts/ToastContext';
import { useAuth } from '../hooks/useAuth';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { PublicLayout } from '../layouts/PublicLayout';
import { ClassesPage } from '../pages/Dashboard/ClassesPage';
import { ApplicationsPage } from '../pages/Dashboard/ApplicationsPage';
import { ViewStudentPage } from '../pages/Dashboard/ApplicationDetailPage';
import { CourseDetailPage } from '../pages/Dashboard/CourseDetailPage';
import { CoursesPage } from '../pages/Dashboard/CoursesPage';
import { DashboardPlaceholderPage } from '../pages/Dashboard/DashboardPlaceholderPage';
import { FinancePage } from '../pages/Dashboard/FinancePage';
import { FeeBalancesPage } from '../pages/Dashboard/FeeBalancesPage';
import { ActiveStudentsPage } from '../pages/Dashboard/ActiveStudentsPage';
import { DroppedStudentsPage } from '../pages/Dashboard/DroppedStudentsPage';
import { GraduatedStudentsPage } from '../pages/Dashboard/GraduatedStudentsPage';
import { LearningBreakStudentsPage } from '../pages/Dashboard/LearningBreakStudentsPage';
import { NeverJoinedStudentsPage } from '../pages/Dashboard/NeverJoinedStudentsPage';
import { InvoicesPage } from '../pages/Dashboard/InvoicesPage';
import { IntakesPage } from '../pages/Dashboard/IntakesPage';
import { IntakeDetailPage } from '../pages/Dashboard/IntakeDetailPage';
import { OverviewPage } from '../pages/Dashboard/OverviewPage';
import { InvoiceViewPage } from '../pages/Dashboard/InvoiceViewPage';
import { SchoolFeeIncomePage } from '../pages/Dashboard/SchoolFeeIncomePage';
import { SchoolFeeReceiptPage } from '../pages/Dashboard/SchoolFeeReceiptPage';
import { StudentLoginPage } from '../pages/Student/StudentLoginPage';
import { StudentPortalPage } from '../pages/Student/StudentPortalPage';
import { StudentClassesPage } from '../pages/Student/StudentClassesPage';
import { StudentFinancePage } from '../pages/Student/StudentFinancePage';
import { StudentInvoiceViewPage } from '../pages/Student/StudentInvoiceViewPage';
import { StudentPaymentReceiptPage } from '../pages/Student/StudentPaymentReceiptPage';
import { StudentAttendancePage } from '../pages/Student/StudentAttendancePage';
import { StudentCoursesPage } from '../pages/Student/StudentCoursesPage';
import { StudentCourseDetailPage } from '../pages/Student/StudentCourseDetailPage';
import { StudentProfilePage } from '../pages/Student/StudentProfilePage';
import { StudentDownloadsPage } from '../pages/Student/StudentDownloadsPage';
import { SettingsPage } from '../pages/Dashboard/SettingsPage';
import { SiteBuilderDashboardPage } from '../pages/Dashboard/SiteBuilderDashboardPage';
import { StudentsPage } from '../pages/Dashboard/StudentsPage';
import { TeamPage } from '../pages/Dashboard/TeamPage';
import { EmployeesPage } from '../pages/Dashboard/EmployeesPage';
import { EmployeeDetailPage, EmployeeEditPage } from '../pages/Dashboard/EmployeeDetailPage';
import { PaySalariesPage } from '../pages/Dashboard/PaySalariesPage';
import { PaymentLedgerPage } from '../pages/Dashboard/PaymentLedgerPage';
import { LandingPage } from '../pages/Landing/LandingPage';
import { PlatformPage } from '../pages/Platform/PlatformPage';
import { SiteBuilderPage } from '../pages/Platform/SiteBuilderPage';
import { AudiencePage } from '../pages/Audience/AudiencePage';
import { PrivateTutorsPage } from '../pages/Audience/PrivateTutorsPage';
import { SchoolsAcademiesPage } from '../pages/Audience/SchoolsAcademiesPage';
import { LearningCentersPage } from '../pages/Audience/LearningCentersPage';
import { CollegesUniversitiesPage } from '../pages/Audience/CollegesUniversitiesPage';
import { TrainingProvidersPage } from '../pages/Audience/TrainingProvidersPage';
import { EducationTeamsPage } from '../pages/Audience/EducationTeamsPage';
import { InformationPage } from '../pages/Information/InformationPage';
import { AboutUsPage } from '../pages/Information/AboutUsPage';
import { ContactUsPage } from '../pages/Information/ContactUsPage';
import { NotificationsPage } from '../pages/Notifications/NotificationsPage';
import { DashboardNotificationsPage } from '../pages/Dashboard/DashboardNotificationsPage';
import { LoginPage } from '../pages/Login/LoginPage';
import { ForgotPasswordPage } from '../pages/ForgotPassword/ForgotPasswordPage';
import { PublicAcademyPage } from '../pages/PublicAcademy/PublicAcademyPage';
import { PublicClassPage } from '../pages/PublicClass/PublicClassPage';
import { RegisterPage } from '../pages/Register/RegisterPage';
import { SetupPage } from '../pages/Setup/SetupPage';
import { getHostnameContext } from '../utils/hostname';
import { ProtectedRoute, PublicRoute, SetupRoute, StudentProtectedRoute, StudentPublicRoute, TenantRequiredRoute } from './routes';

function TenantScope({ children }) {
  const { user } = useAuth();
  return <TenantProvider user={user}>{children}</TenantProvider>;
}

function RootEntryRoute() {
  const hostnameContext = getHostnameContext(window.location.hostname);

  if (hostnameContext.tenantSlug) {
    return <PublicAcademyPage forcedSlug={hostnameContext.tenantSlug} />;
  }

  return <LandingPage />;
}

function AcademyLoginRoute() {
  const hostnameContext = getHostnameContext(window.location.hostname);
  if (hostnameContext.tenantSlug) {
    return <PublicAcademyPage forcedSlug={hostnameContext.tenantSlug} />;
  }
  return <LoginPage />;
}

const studentStatusPages = [
  { path: 'active', title: 'Active students', description: 'Students who are currently active in your academy.' },
  { path: 'learning-breaks', title: 'Learning breaks', description: 'Students who are on a learning break for now.' },
  { path: 'graduated', title: 'Graduated students', description: 'Students who have completed their studies.' },
  { path: 'never-joined', title: 'Never joined', description: 'Applicants or learners who never started.' },
  { path: 'other', title: 'Dropped students', description: 'Students who have dropped from the academy.' },
];

const analyticsPages = [
  { path: 'active-students-cleared-fees', title: 'Active Students with Cleared Fees' },
  { path: 'active-students-uncleared-fees', title: 'Active Students with Uncleared Fees' },
  { path: 'active-students-paying-fees', title: 'Active Students Paying Fees' },
  { path: 'active-students-not-paying-fees', title: 'Active Students Not Paying Fees' },
  { path: 'fee-collection-standing', title: 'Fee Collection Standing' },
  { path: 'active-attendance-students', title: 'Active Attendance Students' },
  { path: 'inactive-attendance-students', title: 'Inactive Attendance Students' },
  { path: 'daily-attendance-register', title: 'Daily Attendance Register' },
  { path: 'intake-success-trend', title: 'Intake Success Trend' },
  { path: 'sources-of-students', title: 'Sources of Students' },
  { path: 'conversion-by-source', title: 'Conversion by Source' },
  { path: 'trends-over-time', title: 'Trends Over Time' },
  { path: 'course-performance-by-source', title: 'Course Performance by Source' },
  { path: 'demographics-by-source', title: 'Demographics by Source' },
  { path: 'campus-type-distribution', title: 'Campus Type Distribution' },
  { path: 'drop-off-analysis', title: 'Drop-off Analysis' },
  { path: 'application-to-admission-time', title: 'Application to Admission Time' },
  { path: 'session-time-demand', title: 'Session Time Demand' },
  { path: 'distribution-by-country', title: 'Distribution by Country' },
  { path: 'nationality-vs-course-choice', title: 'Nationality vs Course Choice' },
];

const communicationPages = [
  { path: 'sms', title: 'SMS' },
  { path: 'whatsapp', title: 'WhatsApp' },
  { path: 'email', title: 'Email' },
];

const financePages = [
];

function BlankRoute({ title, description }) {
  return <DashboardPlaceholderPage title={title} description={description} />;
}

function AppRoutes() {
  return (
    <TenantScope>
      <Routes>
        <Route element={<PublicLayout />}>
          <Route path="/" element={<RootEntryRoute />} />
          <Route path="/platform" element={<PlatformPage />} />
          <Route path="/platform/site-builder" element={<SiteBuilderPage />} />
          <Route path="/platform/:section" element={<PlatformPage />} />
          <Route path="/who-its-for/private-tutors" element={<PrivateTutorsPage />} />
          <Route path="/who-its-for/schools-academies" element={<SchoolsAcademiesPage />} />
          <Route path="/who-its-for/training-centers" element={<LearningCentersPage />} />
          <Route path="/who-its-for/colleges-universities" element={<CollegesUniversitiesPage />} />
          <Route path="/who-its-for/corporate-training" element={<TrainingProvidersPage />} />
          <Route path="/who-its-for/education-businesses" element={<EducationTeamsPage />} />
          <Route path="/who-its-for/learning-centers" element={<Navigate to="/who-its-for/training-centers" replace />} />
          <Route path="/who-its-for/training-providers" element={<Navigate to="/who-its-for/corporate-training" replace />} />
          <Route path="/who-its-for/education-teams" element={<Navigate to="/who-its-for/education-businesses" replace />} />
          <Route path="/who-its-for/:audience" element={<AudiencePage />} />
          <Route path="/about-us" element={<AboutUsPage />} />
          <Route path="/contact-us" element={<ContactUsPage />} />
          <Route path="/terms-of-service" element={<InformationPage />} />
          <Route path="/data-processing-addendum" element={<InformationPage />} />
          <Route path="/news-and-updates" element={<NotificationsPage />} />
          <Route path="/academy/:slug" element={<PublicAcademyPage />} />
          <Route path="/academy/:slug/classes/:classId" element={<PublicClassPage />} />
          <Route element={<PublicRoute />}>
            <Route path="/login" element={<AcademyLoginRoute />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          </Route>
          <Route element={<StudentPublicRoute />}>
            <Route path="/student/login" element={<StudentLoginPage />} />
          </Route>
        </Route>

        <Route element={<ProtectedRoute />}>
          <Route element={<SetupRoute />}>
            <Route path="/setup" element={<SetupPage />} />
          </Route>
          <Route element={<TenantRequiredRoute />}>
            <Route path="/dashboard" element={<DashboardLayout />}>
              <Route index element={<OverviewPage />} />
              <Route path="academics" element={<DashboardPlaceholderPage title="Academics" description="Courses, classes and intakes live here." />} />
              <Route path="applications" element={<ApplicationsPage />} />
              <Route path="applications/:applicationId" element={<ViewStudentPage />} />
              <Route path="intakes" element={<IntakesPage />} />
              <Route path="intakes/:intakeId" element={<IntakeDetailPage />} />
              <Route path="courses" element={<CoursesPage />} />
              <Route path="courses/:courseId" element={<CourseDetailPage />} />
              <Route path="classes" element={<ClassesPage />} />
              <Route path="students" element={<StudentsPage />} />
              <Route path="students/active" element={<ActiveStudentsPage />} />
              <Route path="students/learning-breaks" element={<LearningBreakStudentsPage />} />
              <Route path="students/other" element={<DroppedStudentsPage />} />
              <Route path="students/graduated" element={<GraduatedStudentsPage />} />
              <Route path="students/never-joined" element={<NeverJoinedStudentsPage />} />
              {studentStatusPages.filter((item) => !['active', 'learning-breaks', 'other', 'graduated', 'never-joined'].includes(item.path)).map((item) => (
                <Route
                  key={item.path}
                  path={`students/${item.path}`}
                  element={<BlankRoute title={item.title} description={item.description} />}
                />
              ))}
              <Route path="analytics" element={<BlankRoute title="Data Analytics" description="Blank analytics workspace for now." />} />
              {analyticsPages.map((item) => (
                <Route
                  key={item.path}
                  path={`analytics/${item.path}`}
                  element={<BlankRoute title={item.title} description="Blank analytics page for now." />}
                />
              ))}
              <Route path="communication" element={<BlankRoute title="Communication" description="Blank communications workspace for now." />} />
              {communicationPages.map((item) => (
                <Route
                  key={item.path}
                  path={`communication/${item.path}`}
                  element={<BlankRoute title={item.title} description="Blank communications page for now." />}
                />
              ))}
              <Route path="finance" element={<FinancePage />} />
              <Route path="finance/invoices" element={<InvoicesPage />} />
              <Route path="finance/invoices/:invoiceId" element={<InvoiceViewPage />} />
              <Route path="finance/fee-balances" element={<FeeBalancesPage />} />
              <Route path="finance/school-fee-income" element={<SchoolFeeIncomePage />} />
              <Route path="finance/school-fee-income/:transactionId/receipt" element={<SchoolFeeReceiptPage />} />
              <Route path="notifications" element={<DashboardNotificationsPage />} />
              {financePages.map((item) => (
                <Route
                  key={item.path}
                  path={`finance/${item.path}`}
                  element={<BlankRoute title={item.title} description="Blank finance page for now." />}
                />
              ))}
              <Route path="hr" element={<BlankRoute title="HR" description="Blank HR workspace for now." />} />
              <Route path="hr/employees/:employeeId/edit" element={<EmployeeEditPage />} />
              <Route path="hr/employees/:employeeId" element={<EmployeeDetailPage />} />
              <Route path="hr/employees" element={<EmployeesPage />} />
              <Route path="hr/payroll/pay-salaries" element={<PaySalariesPage />} />
              <Route path="hr/payroll/payment-ledger" element={<PaymentLedgerPage />} />
              <Route path="team" element={<TeamPage />} />
              <Route path="site-builder" element={<SiteBuilderDashboardPage />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>
            <Route element={<DashboardLayout />}>
              <Route path="/view-student" element={<ViewStudentPage />} />
            </Route>
          </Route>
        </Route>

        <Route element={<StudentProtectedRoute />}>
          <Route path="/student/portal" element={<StudentPortalPage />} />
          <Route path="/student/classes" element={<StudentClassesPage />} />
          <Route path="/student/invoices" element={<StudentFinancePage view="invoices" />} />
          <Route path="/student/invoices/:invoiceId" element={<StudentInvoiceViewPage />} />
          <Route path="/student/fee-payments" element={<StudentFinancePage view="payments" />} />
          <Route path="/student/fee-payments/:paymentId" element={<StudentPaymentReceiptPage />} />
          <Route path="/student/downloads" element={<StudentDownloadsPage />} />
          <Route path="/student/attendance" element={<StudentAttendancePage />} />
          <Route path="/student/courses" element={<StudentCoursesPage />} />
          <Route path="/student/courses/:tenantId/:courseId" element={<StudentCourseDetailPage />} />
          <Route path="/student/profile" element={<StudentProfilePage />} />
          <Route path="/student" element={<Navigate to="/student/portal" replace />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </TenantScope>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <AppRoutes />
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
